import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"

export default class extends Controller {
  static values = { url: String }
  static targets = [ "map", "spinner", "modeToggle"  ]
  connect() {
    const mapTarget = this.hasMapTarget ? this.mapTarget : this.element

    const savedView = localStorage.getItem("fc_current_view") || "dayGridWeek"
    const savedDate = localStorage.getItem("fc_current_date") || null
    const savedDetailedMode = localStorage.getItem("fc_detailed_mode") === "true"

    if (this.hasModeToggleTarget) {
      this.modeToggleTarget.checked = savedDetailedMode
    }
    if (savedDetailedMode) {
      mapTarget.classList.remove("is-simple-mode")
    } else {
      mapTarget.classList.add("is-simple-mode")
    }

    this.calendar = new Calendar(mapTarget, {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: savedView,
      initialDate: savedDate,
      locale: "ru",
      firstDay: 1,
      height: "auto",
      weekends: false,
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,dayGridWeek"
      },
      buttonText: {
        today: "Сегодня",
        month: "Месяц",
        week: "Неделя"
      },
      eventTextColor: '#333333',
      events: this.urlValue,
      eventClassNames: 'p-0 overflow-hidden rounded-md border-0 shadow-sm',
      loading: (isLoading) => {
        this.toggleSpinner(isLoading)
      },
      datesSet: (dateInfo) => {
        localStorage.setItem("fc_current_view", dateInfo.view.type)
        localStorage.setItem("fc_current_date", this.calendar.getDate().toISOString())
      },
      eventContent: (arg) => {
        if (arg.event.extendedProps.isTotal) {
          const isUnderLimit = arg.event.extendedProps.underLimit;
          const isVacation = arg.event.extendedProps.isVacation;
          const vacationKind = arg.event.extendedProps.vacationKind;
          const isHoliday = arg.event.extendedProps.isHoliday;

          let textColorClass = "text-green-800";
          if (isUnderLimit) {
            textColorClass = "text-rose-700";
          } else if (isHoliday) {
            textColorClass = "text-amber-700 font-medium";
          } else if (isVacation) {
            if (vacationKind === "sick") textColorClass = "text-red-800 font-medium";
            else if (vacationKind === "time_off") textColorClass = "text-blue-900 font-medium";
            else textColorClass = "text-orange-600 font-normal";
          }

          return {
            html: `<div class="w-full text-center font-mono space-x-0.5 text-[11px] font-bold ${textColorClass}">${arg.event.title}</div>`
          }
        }

        const themeTitle = arg.event.title
        const time = arg.event.extendedProps.time
        const entry = arg.event.extendedProps.entry
        const comment = arg.event.extendedProps.comment
        const activity = arg.event.extendedProps.activity

        const html = `
        <div class="flex flex-col p-1.5 min-w-0 text-sm leading-tight">
          <div class="flex justify-between items-start gap-1">
            <div class="text-xs text-slate-400 flex items-center gap-0.5">
              <span class="shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-3">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clip-rule="evenodd" />
                </svg>
              </span>
              <span>${time}</span>
            </div>
            <span class="text-slate-700 text-xs shrink-0 hover:underline hover:text-orange-700 js-entry-link" data-entry="${entry}">${entry}</span>
          </div>
          
          <div class="js-details-block hidden mt-1.5 pt-1.5 border-t border-slate-100">
            <div class="font-semibold multi-title my-1">${themeTitle}</div>
            <div class="font-light text-slate-600 text-xs italic multi-title py-1">${comment}</div>
            <div class="flex justify-end mt-1">
              <span class="inline-flex items-center rounded-lg bg-gray-50/50 px-2 py-1 text-[10px] font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                ${activity}
              </span>
            </div>
          </div>
        </div>
      `
        return { html: html }
      },


      eventClick: this.handleEventClick.bind(this)
    })

    this.calendar.render();
    mapTarget.M_FullCalendar = this.calendar
  }

  disconnect() {
    if (this.calendar) this.calendar.destroy()
  }

  toggleSpinner(isLoading) {
    if (!this.hasSpinnerTarget) return

    const mapTarget = this.hasMapTarget ? this.mapTarget : this.element

    if (isLoading) {
      this.spinnerTarget.classList.remove("hidden")
      mapTarget.classList.add("opacity-40", "pointer-events-none")
    } else {
      this.spinnerTarget.classList.add("hidden")
      mapTarget.classList.remove("opacity-40", "pointer-events-none")
    }
  }

  toggleViewMode() {
    if (!this.hasMapTarget || !this.hasModeToggleTarget) return

    const isDetailed = this.modeToggleTarget.checked
    localStorage.setItem("fc_detailed_mode", isDetailed)

    if (isDetailed) {
      this.mapTarget.classList.remove('is-simple-mode')
    } else {
      this.mapTarget.classList.add('is-simple-mode')
    }

    if (this.calendar) {
      this.calendar.updateSize()
    }
  }


  handleEventClick(info) {
    const target = info.jsEvent.target;

    if (target.classList.contains('js-entry-link')) {
      info.jsEvent.stopPropagation();
      const entryId = target.getAttribute('data-entry');
      window.open(`/entries/${entryId}`, '_blank', 'noopener,noreferrer');
      return;
    }

    const detailsBlock = info.el.querySelector('.js-details-block');
    if (detailsBlock) {
      detailsBlock.classList.toggle('hidden');

      this.calendar.updateSize();
    }
  }
}
